import React, { useState } from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Typography
} from '@material-ui/core';
import { useSnapshot } from 'valtio';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import DialogWithEnterAction from '../../components/DialogWithEnterAction';
import { ActionButton } from '../../components/ActionButton';
import { useControllerConnectionStatus } from '../../Hooks/useControllerConnectionStatus';
import { ShowSetting } from '../../FormWidgets/ShowSliderSetting';
import { ValueBox } from '../../FormWidgets/SettingGroup';
import { Select } from '../../FormWidgets/Select';
import SettingsSwitch from '../../FormWidgets/SettingsSwitch';
import Desc, { GetHelp, InfoButton } from '../../FormWidgets/Description';
import { MyTimePicker } from '../../FormWidgets/DatePicker';
import { makeStyles } from '@material-ui/core/styles';
import {
  formatTimeParts,
  secondsToTimeParts,
  readTimeParts,
  timePartsToSeconds
} from '../../Utils/time_utils';
import { HandleAction } from '../../Utils/utils';
import { nutritionScope, supportsTankRefill } from '../../Utils/nutrition_utils';
import getStateManager from '../../StateManager';

const MAX_FILL_SECONDS = 4 * 60 * 60;

const useStyles = makeStyles((theme) => ({
  unsaved: {
    '& .MuiInput-underline:after': {
      borderBottom: `2px solid ${theme.palette.secondary.main}`,
      transform: 'scaleX(1)'
    }
  }
}));

export default function TankRefillControls({
  controller,
  parent,
  tank,
  draft,
  controls,
  disabled
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const durationHelp = GetHelp('tank_fill_timeout_seconds');
  const manualHelp = GetHelp('tank_refill_now');
  const snapshot = useSnapshot(controller);
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [duration, setDuration] = useState(1200);
  const [error, setError] = useState('');
  const path = (key) => 'tanks.' + tank.tank_id + '.' + key;
  const timed = draft.fill_timeout_seconds > 0;
  const send = (seconds) => {
    if (controller.connection?.status !== 'good') return;
    const current = parent.settings.nutrition?.tanks?.find((row) => row.tank_id === tank.tank_id);
    const info = parent.settings.irrigation_tank_info?.find((row) => row.tank_id === tank.tank_id);
    if (
      !controller.access_rights?.nutrition?.write ||
      !supportsTankRefill(controller, current) ||
      info?.owner_zone !== nutritionScope(controller, parent)
    ) {
      setError(t('tank_action_unavailable'));
      return;
    }
    if (!Number.isInteger(seconds) || seconds < 0 || seconds > MAX_FILL_SECONDS) {
      setError(t('tank_refill_duration_invalid'));
      return;
    }
    try {
      if (
        HandleAction(
          getStateManager().wsManager,
          controller,
          'action_tank_refill_' + tank.tank_id,
          seconds,
          parent.zone_id
        ) === false
      )
        throw new Error('send failed');
      enqueueSnackbar(t('tank_action_submitted'), { variant: 'info' });
      setOpen(false);
      setError('');
    } catch (_) {
      setError(t('tank_action_send_failed'));
    }
  };
  const start = () => {
    const seconds = Number(duration);
    if (!Number.isInteger(seconds) || seconds < 1 || seconds > MAX_FILL_SECONDS)
      setError(t('tank_refill_duration_invalid'));
    else send(seconds);
  };
  const unavailable =
    disabled || !snapshot.access_rights?.nutrition?.write || connectionStatus !== 'good';
  return (
    <>
      <ValueBox controller={controller} id="tank_refill_strategy" style={{ whiteSpace: 'normal' }}>
        <Typography id={path('strategy-label')}>
          <Desc id="tank_refill_strategy" />
        </Typography>
        <Select
          fullWidth
          id={path('strategy')}
          labelId={path('strategy-label')}
          value={draft.refill_strategy}
          controller_value={tank.refill_strategy}
          disabled={disabled}
          onChange={(event) =>
            controls.setValue(path('refill_strategy'), Number(event.target.value))
          }
        >
          {[0, 1, 2].map((value) => (
            <MenuItem key={value} value={value} disabled={value === 2 && timed}>
              {t(['tank_refill_off', 'tank_refill_low', 'tank_refill_planned'][value])}
            </MenuItem>
          ))}
          {![0, 1, 2].includes(draft.refill_strategy) && (
            <MenuItem value={draft.refill_strategy} disabled>
              {t('unknown')} ({draft.refill_strategy})
            </MenuItem>
          )}
        </Select>
      </ValueBox>
      <ValueBox
        controller={controller}
        id="tank_fill_timeout_seconds"
        style={{ whiteSpace: 'normal' }}
      >
        <Box display="flex" alignItems="center">
          <FormControlLabel
            style={{ marginRight: 0 }}
            label={t('tank_fill_timeout_enabled')}
            control={
              <SettingsSwitch
                color="primary"
                checked={timed}
                controller_value={tank.fill_timeout_seconds > 0}
                disabled={disabled || draft.refill_strategy === 2}
                onChange={(_, checked) =>
                  controls.setValue(path('fill_timeout_seconds'), checked ? 1200 : 0)
                }
              />
            }
          />
          <InfoButton {...durationHelp} ariaLabel={t('tank_fill_timeout_seconds') + ' Info'} />
        </Box>
        <MyTimePicker
          id={path('fill_timeout_seconds')}
          label={t('tank_fill_timeout_seconds')}
          fullWidth
          seconds
          minSeconds={1}
          maxSeconds={MAX_FILL_SECONDS}
          value={formatTimeParts(secondsToTimeParts(draft.fill_timeout_seconds || 1200), true)}
          disabled={disabled || !timed}
          className={
            draft.fill_timeout_seconds !== tank.fill_timeout_seconds ? classes.unsaved : undefined
          }
          onChange={(date) =>
            controls.setValue(path('fill_timeout_seconds'), timePartsToSeconds(readTimeParts(date)))
          }
        />
      </ValueBox>
      {draft.refill_strategy === 2 && (
        <ShowSetting
          controller={controller}
          id={path('planned_offset_minutes')}
          show="tank_planned_offset_minutes"
          cur_value={draft.planned_offset_minutes}
          controller_value={tank.planned_offset_minutes}
          min={-720}
          max={720}
          unit="min"
          disabled={disabled}
          controls={controls}
        />
      )}
      <ValueBox controller={controller} id="tank_refill_now">
        <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: '0.5rem' }}>
          <Box display="flex" alignItems="center">
            <ActionButton
              title={connectionDisabledReason}
              disabled={unavailable || tank.selected_route_id === 0}
              onClick={() => {
                setDuration(Math.min(tank.fill_timeout_seconds || 1200, MAX_FILL_SECONDS));
                setError('');
                setOpen(true);
              }}
            >
              {t('tank_refill_now')}
            </ActionButton>
            <InfoButton {...manualHelp} ariaLabel={t('tank_refill_now') + ' Info'} />
          </Box>
          <ActionButton title={connectionDisabledReason} disabled={unavailable} onClick={() => send(0)}>
            {t('tank_refill_stop')}
          </ActionButton>
        </Box>
        {error && !open && (
          <Typography role="alert" color="error">
            {error}
          </Typography>
        )}
      </ValueBox>
      <DialogWithEnterAction
        open={open}
        onClose={() => setOpen(false)}
        onEnterAction={start}
        enterActionDisabled={unavailable}
        aria-labelledby={path('dialog-title')}
      >
        <DialogTitle id={path('dialog-title')}>
          {t('tank_refill_now')} — {t('tank_id', { id: tank.tank_id })}
          <InfoButton {...manualHelp} ariaLabel={t('tank_refill_now') + ' Info'} />
        </DialogTitle>
        <DialogContent>
          <MyTimePicker
            id={path('manual-duration')}
            fullWidth
            seconds
            minSeconds={1}
            maxSeconds={MAX_FILL_SECONDS}
            label={t('tank_fill_timeout_seconds')}
            value={formatTimeParts(secondsToTimeParts(duration), true)}
            disabled={unavailable}
            onChange={(date) => {
              setDuration(timePartsToSeconds(readTimeParts(date)));
              setError('');
            }}
          />
          <Box display="flex" flexWrap="wrap" style={{ gap: 8, margin: '8px 0' }}>
            {[5, 15, 30, 60].map((minutes) => (
              <Button
                key={minutes}
                color="primary"
                variant={duration === minutes * 60 ? 'contained' : 'outlined'}
                aria-pressed={duration === minutes * 60}
                disabled={unavailable}
                onClick={() => {
                  setDuration(minutes * 60);
                  setError('');
                }}
              >
                {minutes} min
              </Button>
            ))}
          </Box>
          {error && (
            <Typography role="alert" color="error">
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <ActionButton title={connectionDisabledReason} disabled={unavailable} onClick={start}>
            {t('apply')}
          </ActionButton>
        </DialogActions>
      </DialogWithEnterAction>
    </>
  );
}
